import { catchError, map } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { of } from 'rxjs';
import { Router, CanActivateFn } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { SubscriptionService } from '../services/subscription.service';

/** Rotas que o usuário pode acessar mesmo com assinatura expirada ou não paga (para regularizar). */
const ALLOWED_WHEN_EXPIRED_OR_PAST_DUE = ['/settings/billing', '/settings/current-subscription', '/settings/subscriptions'];

function isAllowedWhenExpiredOrPastDue(url: string): boolean {
  const path = (url || '').split('?')[0];
  return ALLOWED_WHEN_EXPIRED_OR_PAST_DUE.some((allowed) => path.startsWith(allowed));
}

/**
 * Guard que verifica se a assinatura está ativa antes de permitir acesso à rota.
 * Se a assinatura estiver expirada ou com pendência (não paga), redireciona para a tela de pagamento.
 * Permite acesso a /settings/billing e /settings/assinatura-vigente para o usuário regularizar.
 */
export const subscriptionGuard: CanActivateFn = (route, state) => {
  const subscriptionService = inject(SubscriptionService);
  const router = inject(Router);
  const translate = inject(TranslateService);
  const url = state.url || '';

  return subscriptionService.getCurrentSubscription()
    .pipe(
      map((subscription) => {
        // Se não houver assinatura: permitir tela de assinatura (para escolher plano/trial) ou billing
        if (!subscription) {
          if (isAllowedWhenExpiredOrPastDue(url)) return true;
          if (url.startsWith('/settings/subscriptions')) return true;
          router.navigate(['/settings/billing'], {
            queryParams: {
              reason: 'NO_SUBSCRIPTION',
              message: translate.instant('subscriptionGuard.noSubscription')
            }
          });
          return false;
        }

        // Expirada ou não paga (PastDue): permitir só tela de pagamento/assinatura
        if (subscription.status === 'Expired' || subscription.status === 'PastDue') {
          if (isAllowedWhenExpiredOrPastDue(url)) return true;
          router.navigate(['/settings/billing'], {
            queryParams: {
              reason: subscription.status === 'Expired' ? 'SUBSCRIPTION_EXPIRED' : 'SUBSCRIPTION_PAST_DUE',
              message: subscription.status === 'Expired'
                ? translate.instant('subscriptionGuard.subscriptionExpired')
                : translate.instant('subscriptionGuard.subscriptionPastDue')
            }
          });
          return false;
        }

        // Verificar se trial expirou
        if (subscription.status === 'Trialing' &&
            subscription.trialDaysRemaining !== null &&
            subscription.trialDaysRemaining !== undefined &&
            subscription.trialDaysRemaining <= 0) {
          if (isAllowedWhenExpiredOrPastDue(url)) return true;
          router.navigate(['/settings/billing'], {
            queryParams: {
              reason: 'TRIAL_EXPIRED',
              message: translate.instant('subscriptionGuard.trialExpired')
            }
          });
          return false;
        }

        // Assinatura ativa ou trial válido - permitir acesso
        return true;
      }),
      catchError((error: unknown) => {
        // Se o erro for de rede/servidor (API ou banco fora), permitir acesso para não gerar tela branca
        const isConnectionOrServer = error instanceof HttpErrorResponse &&
          (error.status === 0 || (error.status >= 500 && error.status < 600));
        if (isConnectionOrServer) {
          return of(true);
        }
        // Outros erros: redirecionar para billing
        router.navigate(['/settings/billing'], {
          queryParams: {
            reason: 'NO_SUBSCRIPTION',
            message: translate.instant('subscriptionGuard.checkError')
          }
        });
        return of(false);
      })
    );
};
